import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Search, FileText, Upload, Trash2 } from "lucide-react";
import ExportCsvButton from "@/components/ExportCsvButton";
import { Checkbox } from "@/components/ui/checkbox";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const STATUT_LABEL = { brouillon: "Brouillon", signe: "Signé", cloture: "Clôturé" };
const TYPE_LABEL = { entree: "Entrée", sortie: "Sortie", entree_sortie: "Entrée + sortie" };

export default function ConstatsList() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [statut, setStatut] = useState("all");
  const [type, setType] = useState("all");
  const [importe, setImporte] = useState(new URLSearchParams(window.location.search).get("importe") === "1");
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState([]);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const { user } = useAuth();
  const canDelete = (it) => !["signe", "cloture"].includes(it.statut) || user?.peut_supprimer_verrouille;

  const toggle = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const toggleAll = () => {
    const ids = items.filter(canDelete).map((it) => it.id);
    setSelected((s) => (s.length === ids.length ? [] : ids));
  };

  const deleteSelected = async () => {
    const results = await Promise.allSettled(selected.map((id) => api.delete(`/etats/${id}`)));
    const ok = results.filter((r) => r.status === "fulfilled").length;
    const ko = results.find((r) => r.status === "rejected");
    if (ok) toast.success(`${ok} constat${ok > 1 ? "s" : ""} déplacé${ok > 1 ? "s" : ""} dans la corbeille (30 jours)`);
    if (ko) toast.error(apiError(ko.reason?.response?.data?.detail));
    setSelected([]); setConfirmOpen(false); load();
  };

  const load = async () => {
    setLoading(true);
    const params = {};
    if (search) params.search = search;
    if (statut !== "all") params.statut = statut;
    if (type !== "all") params.type_constat = type;
    if (importe) params.importe = "1";
    const { data } = await api.get("/etats", { params });
    setItems(data);
    setLoading(false);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [statut, type, importe]);

  const statutColor = (s) =>
    s === "signe" ? "bg-dd-blue text-white"
      : s === "cloture" ? "bg-dd-night text-white"
      : "bg-dd-light text-dd-night";

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-3xl font-bold text-dd-blue">Constats d'état des lieux</h1>
          <p className="text-sm text-dd-warm mt-1">Remise et restitution de la chose louée.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {selected.length > 0 && (
            <Button data-testid="delete-selected-btn" variant="outline" onClick={() => setConfirmOpen(true)}
              className="h-11 border-destructive text-destructive hover:bg-destructive/10">
              <Trash2 size={18} className="mr-2" /> Supprimer ({selected.length})
            </Button>
          )}
          <ExportCsvButton />
          <Button data-testid="import-constat-btn" variant="outline" onClick={() => navigate("/constats/importer")}
            className="h-11 border-dd-blue text-dd-blue hover:bg-dd-light">
            <Upload size={18} className="mr-2" /> Importer un constat
          </Button>
          <Button data-testid="new-constat-btn" onClick={() => navigate("/constats/nouveau")}
            className="h-11 bg-dd-blue hover:bg-dd-night text-white">
            <Plus size={18} className="mr-2" /> Nouveau constat
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap gap-3 mb-5">
        <form onSubmit={(e) => { e.preventDefault(); load(); }} className="relative flex-1 min-w-[240px]">
          <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-dd-warm" />
          <Input data-testid="search-input" value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Immeuble, locataire, n° de dossier…" className="pl-10 h-11 bg-white" />
        </form>
        <Select value={statut} onValueChange={setStatut}>
          <SelectTrigger className="w-44 h-11 bg-white" data-testid="filter-statut"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous les statuts</SelectItem>
            <SelectItem value="brouillon">Brouillon</SelectItem>
            <SelectItem value="signe">Signé</SelectItem>
            <SelectItem value="cloture">Clôturé</SelectItem>
          </SelectContent>
        </Select>
        <Select value={type} onValueChange={setType}>
          <SelectTrigger className="w-44 h-11 bg-white" data-testid="filter-type"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous les types</SelectItem>
            <SelectItem value="entree">Entrée</SelectItem>
            <SelectItem value="sortie">Sortie</SelectItem>
            <SelectItem value="entree_sortie">Entrée + sortie</SelectItem>
          </SelectContent>
        </Select>
        <Button variant={importe ? "default" : "outline"} onClick={() => setImporte((v) => !v)} data-testid="filter-importe"
          className={`h-11 ${importe ? "bg-dd-blue text-white hover:bg-dd-night" : "bg-white border-dd-light text-dd-night"}`}>
          <Upload size={16} className="mr-2" /> Importés à relire
        </Button>
      </div>

      <div className="bg-white rounded-lg border border-dd-light overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-dd-blue text-white text-left">
              <th className="px-4 py-3 w-10">
                <Checkbox checked={items.filter(canDelete).length > 0 && selected.length === items.filter(canDelete).length} onCheckedChange={toggleAll}
                  className="border-white data-[state=checked]:bg-white data-[state=checked]:text-dd-blue" data-testid="select-all-checkbox" />
              </th>
              <th className="px-4 py-3 font-semibold">Dossier</th>
              <th className="px-4 py-3 font-semibold">Immeuble / objet</th>
              <th className="px-4 py-3 font-semibold">Locataire</th>
              <th className="px-4 py-3 font-semibold">Type</th>
              <th className="px-4 py-3 font-semibold">Date</th>
              <th className="px-4 py-3 font-semibold">Statut</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} className="px-4 py-10 text-center text-dd-warm">Chargement…</td></tr>
            ) : items.length === 0 ? (
              <tr><td colSpan={7} className="px-4 py-16 text-center text-dd-warm">
                <FileText className="mx-auto mb-3 text-dd-light" size={40} />
                Aucun constat. Créez votre premier constat.
              </td></tr>
            ) : items.map((it) => (
              <tr key={it.id} data-testid={`constat-row-${it.id}`}
                onClick={() => navigate(`/constats/${it.id}`)}
                className={`border-t border-dd-light hover:bg-dd-light/40 cursor-pointer ${selected.includes(it.id) ? "bg-dd-light/60" : ""}`}>
                <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                  <Checkbox checked={selected.includes(it.id)} disabled={!canDelete(it)} onCheckedChange={() => toggle(it.id)} data-testid={`select-${it.id}`} />
                </td>
                <td className="px-4 py-3 font-medium text-dd-blue">
                  {it.numero_dossier || "—"}
                  {it.importe && <Badge className="ml-2 bg-amber-100 text-amber-900 border-0 text-[10px] align-middle" data-testid={`badge-importe-${it.id}`}>Importé</Badge>}
                </td>
                <td className="px-4 py-3">
                  <div className="text-dd-night">{it.immeuble || "—"}</div>
                  <div className="text-xs text-dd-warm">{[it.npa, it.localite].filter(Boolean).join(" ")} {it.numero_objet ? `· obj. ${it.numero_objet}` : ""}</div>
                </td>
                <td className="px-4 py-3 text-dd-night">{it.locataire_entrant || it.locataire_sortant || "—"}</td>
                <td className="px-4 py-3">{TYPE_LABEL[it.type_constat]}</td>
                <td className="px-4 py-3 text-dd-night">{it.date_constat}</td>
                <td className="px-4 py-3">
                  <Badge className={`${statutColor(it.statut)} border-0`}>{STATUT_LABEL[it.statut]}</Badge>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent data-testid="delete-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer {selected.length > 1 ? `${selected.length} constats` : "ce constat"} ?</AlertDialogTitle>
            <AlertDialogDescription>Le{selected.length > 1 ? "s" : ""} constat{selected.length > 1 ? "s" : ""} ser{selected.length > 1 ? "ont" : "a"} déplacé{selected.length > 1 ? "s" : ""} dans la corbeille et restaurable{selected.length > 1 ? "s" : ""} pendant 30 jours, puis effacé{selected.length > 1 ? "s" : ""} définitivement. Continuer ?</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="h-11" data-testid="delete-cancel">Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={deleteSelected} className="h-11 bg-destructive hover:bg-destructive/90 text-white" data-testid="delete-confirm">Supprimer définitivement</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
